/**
 * The Pool module's error surface. A 403 from ANY /pool/* endpoint means "not on the pool.operator
 * allow-list" — a completely separate gate from the RBAC roles/permissions the rest of the portal
 * uses (see api/types.ts's Pool banner) — so it gets its own copy, never the generic
 * state.forbiddenTitle/Body that <ErrorState/> falls back to for every other 403 in the app.
 */

import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { ErrorState } from "@/components/ui";

export interface PoolErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

/** True when the error is a 403 from a /pool/* call — i.e. the caller is not a pool operator. */
export function isNotPoolOperator(error: unknown): boolean {
  return error instanceof ApiRequestError && error.error.status === 403;
}

export function PoolErrorState({ error, onRetry, className }: PoolErrorStateProps) {
  const { t } = useTranslation();

  if (isNotPoolOperator(error)) {
    return (
      <ErrorState
        error={error}
        title={t("pool.notOperatorTitle")}
        body={t("pool.notOperatorBody")}
        {...(className ? { className } : {})}
      />
    );
  }

  return (
    <ErrorState
      error={error}
      {...(onRetry ? { onRetry } : {})}
      {...(className ? { className } : {})}
    />
  );
}
